import { createContext, useContext, useState, useEffect, useMemo } from 'react';

const AmenitiesContext = createContext(null);

/**
 * Provider component that fetches and caches the canonical amenities list from the API.
 * Wrap your app with this to make amenities available throughout the component tree.
 */
export function AmenitiesProvider({ apiClient, children }) {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAmenities() {
      try {
        setLoading(true);
        const data = await apiClient.listAmenities();
        setAmenities(data || []);
        setError(null);
      } catch (err) {
        console.error('[AmenitiesProvider] Failed to fetch amenities:', err);
        setError(err);
        setAmenities([]);
      } finally {
        setLoading(false);
      }
    }

    fetchAmenities();
  }, [apiClient]);

  // Memoize the grouped amenities to prevent infinite re-renders
  const groupedAmenities = useMemo(() => {
    const groups = {};
    amenities.forEach(a => {
      if (!groups[a.category]) groups[a.category] = [];
      groups[a.category].push(a);
    });
    return groups;
  }, [amenities]);

  const value = {
    amenities,
    loading,
    error,
    // Helper to get a single amenity config by key
    getAmenity: (key) => amenities.find(a => a.key === key),
    // Helper to get amenities by category
    getAmenitiesByCategory: (category) => amenities.filter(a => a.category === category),
    // Helper to group all amenities by category (memoized)
    getGroupedAmenities: () => groupedAmenities
  };

  return (
    <AmenitiesContext.Provider value={value}>
      {children}
    </AmenitiesContext.Provider>
  );
}

/**
 * Hook to access the amenities context
 * @returns {Object} { amenities, loading, error, getAmenity, getAmenitiesByCategory, getGroupedAmenities }
 */
export function useAmenities() {
  const context = useContext(AmenitiesContext);
  if (!context) {
    throw new Error('useAmenities must be used within AmenitiesProvider');
  }
  return context;
}

/**
 * Hook to get a single amenity config by key
 * @param {string} key - The amenity key
 * @returns {Object|null} The amenity config or null if not found
 */
export function useAmenity(key) {
  const { getAmenity } = useAmenities();
  return getAmenity(key);
}
